// @ts-nocheck
// Source of truth for "what is a legal flow" + "what connections are allowed".
// Model: a GREEN buy spine (one buy-out per node) plus optional DOTTED branches
// per wallet (one rotate OR sell exit). Structural rules only — funding is on-chain.

import { LAUNCH_NODE_ID, type EdgeKind, type FlowGraph, type GraphEdge, type SellNode } from "@/lib/algo/graph";
import { resolveDevId } from "@/lib/algo/fundNeeds";

export type Severity = "ok" | "warn" | "block";
export type Issue = { severity: "warn" | "block"; code: string; message: string; edgeId?: string; sellId?: string; bundleId?: string };
export type BundleInfo = { bundleId: string; txCount: number; limit: number; atomic: boolean; splitInto: number; isLaunch: boolean };
export type Validation = {
  ok: boolean;
  severity: Severity;
  issues: Issue[];
  edge: Map<string, Severity>;
  edgeMsg: Map<string, string[]>;
  sell: Map<string, Severity>;
  sellMsg: Map<string, string[]>;
  bundles: BundleInfo[];
};

const worse = (a: Severity, b: Severity): Severity =>
  a === "block" || b === "block" ? "block" : a === "warn" || b === "warn" ? "warn" : "ok";
const BUNDLE_LIMIT = 5;
const LAUNCH_BUYER_LIMIT = 4;

/** Constrain how you draw a connection. A node may have ONE buy-out (green spine)
 *  and ONE dotted branch (rotate). Sells are created separately (not via connect). */
export function canConnect(graph: FlowGraph, source: string, target: string, kind: EdgeKind): { ok: boolean; reason?: string } {
  if (!source || !target) return { ok: false, reason: "invalid connection" };
  if (target === LAUNCH_NODE_ID) return { ok: false, reason: "Launch can't be a target" };
  const byId = new Map(graph.wallets.map((w) => [w.id, w]));
  if (kind === "airdrop") {
    if (!graph.airdropLists?.some(a => a.id === target)) return { ok: false, reason: "unknown airdrop list" };
  } else {
    if (!byId.has(target)) return { ok: false, reason: "unknown wallet" };
  }
  if (kind === "buy") {
    if (graph.edges.some((e) => e.kind === "buy" && e.source === source)) {
      return { ok: false, reason: source === LAUNCH_NODE_ID ? "Launch already starts the flow — continue from the last wallet" : "This wallet already buys next — extend from the end of the line" };
    }
  } else {
    if (source === LAUNCH_NODE_ID) return { ok: false, reason: "Launch can't rotate — it starts the buy flow" };
    if (source === target) return { ok: false, reason: "Can't rotate a wallet into itself" };
    const hasRotate = graph.edges.some((e) => e.kind === "rotate" && e.source === source);
    if (hasRotate) return { ok: false, reason: "This wallet already has a rotate branch" };
  }
  return { ok: true };
}

/** Can this wallet take a new sell branch? (multiple sells allowed per wallet unless 100% rotated) */
export function canSell(graph: FlowGraph, walletId: string): { ok: boolean; reason?: string } {
  if (walletId === LAUNCH_NODE_ID) return { ok: false, reason: "Launch can't sell" };
  if (!graph.wallets.some((w) => w.id === walletId)) return { ok: false, reason: "unknown wallet" };
  const rotatedAll = graph.edges.some((e) => e.kind === "rotate" && e.source === walletId && Number(e.sellPct) >= 100);
  if (rotatedAll) return { ok: false, reason: "This wallet already rotated 100% of its holdings" };
  return { ok: true };
}

export function validateGraph(graph: FlowGraph): Validation {
  const issues: Issue[] = [];
  const edge = new Map<string, Severity>();
  const edgeMsg = new Map<string, string[]>();
  const sell = new Map<string, Severity>();
  const sellMsg = new Map<string, string[]>();
  const byId = new Map(graph.wallets.map((w) => [w.id, w]));
  const isWallet = (id: string) => byId.has(id);
  const isEnabled = (id: string) => byId.get(id)?.enabled === true;

  const flag = (e: GraphEdge | null, severity: "warn" | "block", code: string, message: string, bundleId?: string) => {
    issues.push({ severity, code, message, edgeId: e?.id, bundleId });
    if (e) { edge.set(e.id, worse(edge.get(e.id) ?? "ok", severity)); edgeMsg.set(e.id, [...(edgeMsg.get(e.id) ?? []), message]); }
  };
  const flagSell = (s: SellNode, severity: "warn" | "block", code: string, message: string) => {
    issues.push({ severity, code, message, sellId: s.id });
    sell.set(s.id, worse(sell.get(s.id) ?? "ok", severity));
    sellMsg.set(s.id, [...(sellMsg.get(s.id) ?? []), message]);
  };

  const sorted = [...graph.edges].sort((a, b) => a.order - b.order || a.id.localeCompare(b.id));
  // quantitative remaining fraction per wallet (0..1) — matches simulate()'s
  // "sell % of current holdings" so over-selling is correctly flagged.
  const remain = new Map<string, number>();
  const holds = (id: string) => (remain.get(id) ?? 0) > 1e-9;
  const clampPct = (v: number) => Math.max(0, Math.min(100, Number(v) || 0));

  for (const e of sorted) {
    edge.set(e.id, edge.get(e.id) ?? "ok");
    if (e.target === LAUNCH_NODE_ID) flag(e, "block", "target-launch", "Launch can't be a target");
    else if (e.kind !== "airdrop" && !isWallet(e.target)) flag(e, "block", "target-missing", "Target wallet doesn't exist");
    else if (e.kind !== "airdrop" && !isEnabled(e.target)) flag(e, "block", "target-off", "Target wallet is turned off");

    if (e.kind === "airdrop") {
      if (!graph.airdropLists?.some(a => a.id === e.target)) flag(e, "block", "airdrop-target", "Target airdrop list doesn't exist");
      if (e.source !== LAUNCH_NODE_ID && !isWallet(e.source)) flag(e, "block", "airdrop-source", "Airdrop source doesn't exist");
      if (!(Number(e.sellPct) > 0 && Number(e.sellPct) <= 100)) flag(e, "block", "airdrop-pct", "Airdrop pct must be 1-100%");
      if (isWallet(e.source) && !holds(e.source)) flag(e, "block", "airdrop-no-holdings", "Airdrop source has nothing to drop");
    }

    if (e.kind === "buy" && !(Number(e.sol) > 0)) flag(e, "block", "buy-amount", "Buy amount must be greater than 0");
    if (e.kind === "rotate") {
      if (e.source === LAUNCH_NODE_ID) flag(e, "block", "rotate-from-launch", "Rotate needs a source wallet (not Launch)");
      else if (!isWallet(e.source)) flag(e, "block", "rotate-source-missing", "Rotate source doesn't exist");
      else if (!isEnabled(e.source)) flag(e, "block", "rotate-source-off", "Rotate source is off");
      if (e.source === e.target) flag(e, "block", "rotate-self", "Can't rotate a wallet into itself");
      if (!(Number(e.sellPct) > 0 && Number(e.sellPct) <= 100)) flag(e, "block", "rotate-pct", "Rotate sell must be 1–100%");
      if (e.bundleId) flag(e, "block", "rotate-bundled", "Rotate can't be bundled — it runs as its own step", e.bundleId);
      if (isWallet(e.source) && !holds(e.source)) flag(e, "block", "rotate-no-holdings", "Rotate source has nothing to rotate (never bought, or already fully sold)");
    }
    if (e.kind === "buy" && isWallet(e.target)) remain.set(e.target, 1);
    if (e.kind === "rotate" || e.kind === "airdrop") {
      if (isWallet(e.source) && holds(e.source)) remain.set(e.source, (remain.get(e.source) ?? 0) * (1 - clampPct(e.sellPct) / 100));
      if (e.kind === "rotate" && isWallet(e.target)) remain.set(e.target, 1);
    }
  }

  // sell exit nodes — wallet must hold, valid %, valid trigger, no overdraw.
  const order = { now: 0, time: 1, pct: 2 } as const;
  const sells = [...graph.sells].sort((a, b) => order[a.trigger] - order[b.trigger] || a.value - b.value);
  for (const s of sells) {
    sell.set(s.id, sell.get(s.id) ?? "ok");
    if (!isWallet(s.walletId) || !isEnabled(s.walletId)) { flagSell(s, "block", "sell-wallet", "Sell needs an active wallet"); continue; }
    if (!(s.pct > 0 && s.pct <= 100)) flagSell(s, "block", "sell-pct", "Sell amount must be 1–100%");
    if (s.trigger === "pct" && !(s.value > 0)) flagSell(s, "block", "sell-gain", "Gain target must be greater than 0%");
    if (s.trigger === "time" && !(s.value >= 0)) flagSell(s, "block", "sell-time", "Delay can't be negative");
    if (!holds(s.walletId)) {
      const w = graph.wallets.find(x => x.id === s.walletId);
      const name = w ? w.label : s.walletId;
      flagSell(s, "block", "sell-empty", `Wallet ${name} has nothing to sell (never bought, or already fully sold)`);
    } else if (s.pct > 0 && s.pct <= 100) {
      remain.set(s.walletId, (remain.get(s.walletId) ?? 0) * (1 - clampPct(s.pct) / 100));
    }
  }

  // ---- global topology guardrails ----
  // A LOADED/hand-edited graph bypasses canConnect, so re-enforce the structural
  // rules here: one buy-out per node, one rotate continuation per wallet, no cycles.
  const buyOut = new Map<string, GraphEdge[]>();
  for (const e of graph.edges) if (e.kind === "buy") buyOut.set(e.source, [...(buyOut.get(e.source) ?? []), e]);
  for (const [src, es] of buyOut) {
    if (es.length > 1) for (const e of es) flag(e, "block", "fanout-buy", `${src === LAUNCH_NODE_ID ? "Launch" : "This wallet"} has ${es.length} buy lines — only one buy continuation is allowed`);
  }
  const rotateCount = new Map<string, number>();
  for (const e of graph.edges) if (e.kind === "rotate") rotateCount.set(e.source, (rotateCount.get(e.source) ?? 0) + 1);
  for (const e of graph.edges) if (e.kind === "rotate" && (rotateCount.get(e.source) ?? 0) > 1) flag(e, "block", "multi-rotate", "This wallet has more than one rotate branch — keep only one rotate continuation per wallet");
  // cycle detection over buy+rotate edges (DFS colors)
  const adj = new Map<string, string[]>();
  for (const e of graph.edges) adj.set(e.source, [...(adj.get(e.source) ?? []), e.target]);
  const color = new Map<string, 0 | 1 | 2>();
  let hasCycle = false;
  const visit = (n: string) => {
    color.set(n, 1);
    for (const m of adj.get(n) ?? []) { const c = color.get(m) ?? 0; if (c === 1) { hasCycle = true; return; } if (c === 0) visit(m); }
    color.set(n, 2);
  };
  for (const n of adj.keys()) if ((color.get(n) ?? 0) === 0) visit(n);
  if (hasCycle) issues.push({ severity: "block", code: "cycle", message: "Flow has a cycle — a wallet loops back on itself" });

  // bundles = consecutive same-bundleId runs; launch bundle holds the first edge
  const bundles: BundleInfo[] = [];
  const seenBundles = new Set<string>();
  let i = 0;
  while (i < sorted.length) {
    const e = sorted[i];
    if (!e.bundleId) { i += 1; continue; }
    const startIndex = i;
    const bid = e.bundleId;
    const run: GraphEdge[] = [];
    while (i < sorted.length && sorted[i].bundleId === bid) { run.push(sorted[i]); i += 1; }
    if (seenBundles.has(bid)) for (const x of run) flag(x, "warn", "bundle-split", "This bundle isn't contiguous — it runs as separate slots, not one atomic bundle", bid);
    seenBundles.add(bid);
    const isLaunch = startIndex === 0;
    const txCount = run.reduce((n, x) => n + (x.kind === "rotate" ? 3 : 1), 0);
    const limit = isLaunch ? LAUNCH_BUYER_LIMIT : BUNDLE_LIMIT;
    const atomic = txCount <= limit;
    const splitInto = Math.max(1, Math.ceil(txCount / limit));
    bundles.push({ bundleId: bid, txCount, limit, atomic, splitInto, isLaunch });
    if (!atomic) for (const x of run) flag(x, "warn", "bundle-oversize",
      `Bundle has ${txCount} tx (max ${limit}/slot) — auto-splits into ${splitInto}; only the first is atomic`, bid);
  }

  // ---- creator double-buy guard ----
  // The creator (devWalletId, else the first buyer, else the first wallet) already
  // makes the dev buy. If a buy edge ALSO targets it, that wallet buys twice — the
  // the trap where the creator buys twice (dev buy + a wired buy). Warn (don't block — may be intentional).
  const devBuy = Number(graph.devBuySol) || 0;
  if (devBuy > 0) {
    const creatorId = resolveDevId(graph);
    if (creatorId) {
      for (const e of graph.edges) {
        if (e.kind === "buy" && e.target === creatorId) {
          flag(e, "warn", "creator-double-buy", `${byId.get(creatorId)?.label ?? "This wallet"} is the creator (dev-buys ${devBuy} SOL) — this buy makes it buy twice. Use a separate dev wallet or set dev buy to 0.`);
        }
      }
    }
  }

  const severity = issues.reduce<Severity>((sv, it) => worse(sv, it.severity), "ok");
  const ok = severity !== "block";
  return { ok, severity, issues, edge, edgeMsg, sell, sellMsg, bundles };
}
